import React, { useState } from "react";
import "./Home.css";
import { Users, BarChart3, Clock, Menu, X } from "lucide-react";
import { FaFacebook, FaInstagram, FaXTwitter } from "react-icons/fa6";
import { http } from "../../lib/http";

export default function Home() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    restaurant_name: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    // Basic validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.restaurant_name ||
      !formData.phone ||
      !formData.message
    ) {
      setErrorMessage("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    try {
      await http.post("/fe/contact/send", formData);
      alert("Message sent successfully!");
      setFormData({
        name: "",
        email: "",
        restaurant_name: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      setErrorMessage("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  // build/logo 2025.pdf (5).png
  return (
    <div className="home-container">
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            <img
              src="/logo_meallinkr.png"
              alt="Mealinkr Logo"
              className="navbar-logo"
              onClick={() => (window.location.href = "/home")}
              style={{ cursor: "pointer" }}
            />
          </div>

          <div className={`navbar-links ${isMenuOpen ? "open" : ""}`}>
            <a href="#features" onClick={() => setIsMenuOpen(false)}>
              Features
            </a>
            <a href="#solutions" onClick={() => setIsMenuOpen(false)}>
              Solutions
            </a>
            <a href="#pricing" onClick={() => setIsMenuOpen(false)}>
              Pricing
            </a>
            <a href="#resources" onClick={() => setIsMenuOpen(false)}>
              Resources
            </a>
            <a href="#contact" onClick={() => setIsMenuOpen(false)}>
              Contact
            </a>
            <a
              href="/signin"
              className="btn-signin btn-signin-mobile"
              onClick={() => setIsMenuOpen(false)}
            >
              Sign In
            </a>
          </div>

          <div className="navbar-right">
            <a href="/signin" className="btn-signin btn-signin-desktop">
              Sign In
            </a>
            <button
              type="button"
              className="navbar-toggle"
              aria-label={
                isMenuOpen ? "Close navigation menu" : "Open navigation menu"
              }
              onClick={() => setIsMenuOpen((prev) => !prev)}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* ===== Hero Section ===== */}
      <section id="hero" className="hero">
        <div className="hero-container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="hero-text-group">
                <span className="hero-badge">
                  #1 WhatsApp Ordering Solution
                </span>

                <h1 className="whatsapp-content">
                  Transform Your Restaurant with
                  <span className="whatsapp"> WhatsApp </span>
                  Ordering
                </h1>

                <p className="hero-subtext">
                  Mealinkr's AI-powered chatbot handles customer orders through
                  WhatsApp, increasing sales by 40% while reducing manual work.
                  Get started in minutes!
                </p>
              </div>

              <div className="hero-buttons">
                <a href="/home#contact" className="btn-primary">
                  Get Started Free
                  <span className="arrow">→</span>
                </a>
                <button className="btn-outline">Watch Demo</button>
              </div>

              <div className="hero-features">
                <div className="feature-item">
                  <span className="check">✔</span>
                  <span>2-day free trial</span>
                </div>
                <div className="feature-item">
                  <span className="check">✔</span>
                  <span>Minimal setup fees</span>
                </div>
                <div className="feature-item">
                  <span className="check">✔</span>
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>

            <div className="hero-image-container">
              <img
                src="https://images.unsplash.com/photo-1590935217281-8f102120d683"
                alt="Mobile ordering interface"
                className="hero-image"
              />
              <div className="order-popup">
                <div className="order-popup-content">
                  <div className="order-icon">💬</div>
                  <div>
                    <p className="order-id">Order #12345</p>
                    <p className="order-info">Just received via WhatsApp</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Features Section ===== */}
      <section id="features" className="features-section">
        <div className="features-container">
          <div className="features-header">
            <h2>Powerful Features for Modern Restaurants</h2>
            <p>
              Everything you need to transform your restaurant ordering
              experience
            </p>
          </div>

          <div className="features-grid">
            {/* <!-- Card 1 --> */}
            <div className="feature-card">
              <div className="card-header">
                <img
                  src="https://images.unsplash.com/photo-1614680376408-81e91ffe3db7"
                  alt="WhatsApp Integration"
                />
                <h3>WhatsApp Integration</h3>
              </div>
              <div className="card-content">
                <p>
                  Seamlessly integrate with WhatsApp Business API. Customers
                  order naturally through their favorite messaging app without
                  downloading anything new.
                </p>
                <ul>
                  <li>
                    <span className="check">✔</span> Natural conversation flow
                  </li>
                  <li>
                    <span className="check">✔</span> Multimedia menu sharing
                  </li>
                  <li>
                    <span className="check">✔</span> Order confirmations
                  </li>
                </ul>
              </div>
            </div>

            {/* <!-- Card 2 --> */}
            <div className="feature-card">
              <div className="card-header">
                <img
                  src="https://images.unsplash.com/photo-1647427017067-8f33ccbae493"
                  alt="Restaurant Management"
                />
                <h3>Smart Management</h3>
              </div>
              <div className="card-content">
                <p>
                  Comprehensive dashboard to manage orders, track analytics, and
                  optimize your restaurant operations with real-time insights.
                </p>
                <ul>
                  <li>
                    <span className="check">✔</span> Real-time order tracking
                  </li>
                  <li>
                    <span className="check">✔</span> Sales analytics
                  </li>
                  <li>
                    <span className="check">✔</span> Customer insights
                  </li>
                </ul>
              </div>
            </div>

            {/* <!-- Card 3 --> */}
            <div className="feature-card">
              <div className="card-header">
                <img
                  src="https://images.unsplash.com/photo-1728044849280-10a1a75cff83"
                  alt="Automated Ordering"
                />
                <h3>AI-Powered Automation</h3>
              </div>
              <div className="card-content">
                <p>
                  Advanced AI handles complex orders, upselling, and customer
                  service automatically, freeing your staff for more important
                  tasks.
                </p>
                <ul>
                  <li>
                    <span className="check">✔</span> Intelligent upselling
                  </li>
                  <li>
                    <span className="check">✔</span> 24/7 availability
                  </li>
                  <li>
                    <span className="check">✔</span> Multi-language support
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Solutions Section ===== */}
      <section id="solutions" className="solutions-section">
        <div className="solutions-container">
          <div className="solutions-header">
            <h2>Solutions for Every Restaurant</h2>
            <p>From small cafes to large restaurant chains</p>
          </div>

          <div className="solutions-grid">
            {/* Small Restaurants */}
            <div className="solution-card">
              <div className="icon-wrapper green">
                <Users className="icon" />
              </div>
              <h3>Small Restaurants</h3>
              <p>
                Perfect for family restaurants and local eateries looking to
                digitize their ordering process.
              </p>
              <ul>
                <li>• Easy setup in 15 minutes</li>
                <li>• Affordable pricing</li>
                <li>• Personal support</li>
              </ul>
            </div>

            {/* Growing Chains */}
            <div className="solution-card">
              <div className="icon-wrapper orange">
                <BarChart3 className="icon orange" />
              </div>
              <h3>Growing Chains</h3>
              <p>
                Scale your operations across multiple locations with centralized
                management.
              </p>
              <ul>
                <li>• Multi-location support</li>
                <li>• Advanced analytics</li>
                <li>• Custom integrations</li>
              </ul>
            </div>

            {/* Delivery-First */}
            <div className="solution-card">
              <div className="icon-wrapper green">
                <Clock className="icon" />
              </div>
              <h3>Delivery-First</h3>
              <p>
                Optimize for delivery and takeout with smart order routing and
                timing.
              </p>
              <ul>
                <li>• Delivery tracking</li>
                <li>• Order scheduling</li>
                <li>• Route optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Pricing Section ===== */}
      <section className="pricing-features" id="pricing">
        <div className="container">
          {/* <!-- Section Header --> */}
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Pay only when you earn. No hidden costs.</p>
          </div>

          {/* <!-- Pricing Card --> */}
          <div className="pricing-card">
            {/* <!-- Header Section --> */}
            <div className="row">
              <div className="col">
                <div className="pricing-header">
                  <div className="price-box setup">
                    <p>One-time Setup Fee</p>
                    <span>₹5999</span>
                  </div>
                </div>
              </div>

              <div className="col">
                <div className="pricing-header">
                  <div className="price-box monthly">
                    <p>Monthly</p>
                    <span>12% of your monthly sales</span>
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- Features Section --> */}
            <div className="pricing-features-grid">
              <div className="pricing-feature-list">
                <p>✅ WhatsApp ordering system</p>
                <p>✅ Order management dashboard</p>
                <p>✅ Sales analytics & insights</p>
                <p>✅ Email & chat support</p>
              </div>

              <div className="pricing-feature-list">
                <p>✅ Custom branding</p>
                <p>✅ Menu & item-level control</p>
                <p>✅ Multi-device access</p>
                <p>✅ Priority onboarding</p>
              </div>
            </div>

            {/* <!-- CTA Button --> */}
            <div className="cta">
              <a href="/home#contact" className="btn-primary">
                Get Started
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Resources Section ===== */}
      <section id="resources" className="resources-section">
        <div className="resources-container">
          <div className="resources-header">
            <h2>Resources & Support</h2>
            <p>Everything you need to succeed with Mealinkr</p>
          </div>

          <div className="resources-grid">
            {/* <!-- Card 1 --> */}
            <div className="card">
              <div className="card-header">
                {/* <!-- Shield Icon --> */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon green"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                  />
                </svg>
                <h3 className="card-title">Documentation</h3>
              </div>
              <div className="card-content">
                <p>
                  Comprehensive guides and tutorials to help you get started and
                  maximize your results.
                </p>
              </div>
            </div>

            {/* <!-- Card 2 --> */}
            <div className="card">
              <div className="card-header">
                {/* <!-- Zap Icon -->/ */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon orange"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 2L3 14h7v8l11-12h-8V2z"
                  />
                </svg>
                <h3 className="card-title">Training Videos</h3>
              </div>
              <div className="card-content">
                <p>
                  Step-by-step video tutorials covering setup, customization,
                  and best practices.
                </p>
              </div>
            </div>

            {/* <!-- Card 3 --> */}
            <div className="card">
              <div className="card-header">
                {/* <!-- Headphones Icon --> */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon green"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 14v5a2 2 0 002 2h2v-7H6a2 2 0 00-2 2zm14 0v7h2a2 2 0 002-2v-5a2 2 0 00-2-2h-2zm-7-9a7 7 0 00-7 7v3h14v-3a7 7 0 00-7-7z"
                  />
                </svg>
                <h3 className="card-title">24/7 Support</h3>
              </div>
              <div className="card-content">
                <p>
                  Our dedicated support team is always ready to help you succeed
                  with personalized assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Contact Section ===== */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-header">
            <h2>Get in Touch</h2>
            <p>Ready to transform your restaurant? Let's talk!</p>
          </div>

          <div className="contact-grid">
            {/* <!-- Left Info --> */}
            <div className="contact-info">
              <h3>Contact Information</h3>

              <div className="contact-card">
                <div className="info-list">
                  <div className="info-item">
                    <svg
                      className="icon green"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M6.62 10.79a15.72 15.72 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 12.18 12.18 0 004.54.86 1 1 0 011 1v3.49a1 1 0 01-1 1A17.92 17.92 0 012 6a1 1 0 011-1h3.5a1 1 0 011 1 12.18 12.18 0 00.86 4.54 1 1 0 01-.21 1.11z"
                      />
                    </svg>
                    <div>
                      <p className="label">Phone</p>
                      <p className="text">+91 8823006666</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <svg
                      className="icon green"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M20 2H4a2 2 0 00-2 2v16l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2z"
                      />
                    </svg>
                    <div>
                      <p className="label">WhatsApp</p>
                      <p className="text">+91 8823006666</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <svg
                      className="icon orange"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M20 4H4a2 2 0 00-2 2v1l10 6 10-6V6a2 2 0 00-2-2zM2 8v10a2 2 0 002 2h16a2 2 0 002-2V8l-10 6L2 8z"
                      />
                    </svg>
                    <div>
                      <p className="label">Email</p>
                      <p className="text">mealinkr@gmail.com</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <svg
                      className="icon green"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 112.5-2.5A2.5 2.5 0 0112 11.5z"
                      />
                    </svg>
                    <div>
                      <p className="label">Address</p>
                      <p className="text">Hyderabad, Telangana India</p>
                    </div>
                  </div>
                </div>

                <div className="social-section">
                  <h4>Follow Us</h4>
                  <div className="social-icons">
                    <a
                      href="https://www.facebook.com/mealinkr"
                      className="social-icon green"
                      aria-label="Facebook"
                    >
                      <FaFacebook />
                    </a>
                    <a
                      href="https://x.com/mealinkr"
                      className="social-icon orange"
                      aria-label="Twitter/X"
                    >
                      <FaXTwitter />
                    </a>
                    <a
                      href="https://www.instagram.com/mealinkr/"
                      className="social-icon green"
                      aria-label="Instagram"
                    >
                      <FaInstagram />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- Right Form --> */}
            <div className="contact-form">
              <h3>Send us a message</h3>
              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    name="restaurant_name"
                    placeholder="Restaurant Name"
                    value={formData.restaurant_name}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="phone"
                    placeholder="Enter a Phone Number Without country code"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      handleInputChange({
                        target: { name: "phone", value },
                      });
                    }}
                    maxLength={10}
                    required
                  />
                </div>
                <textarea
                  name="message"
                  rows="4"
                  placeholder="Tell us about your restaurant..."
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
                <button
                  type="submit"
                  className="animated-btn"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <section className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-col">
              <h3 className="footer-title">Mealinkr</h3>
              <p className="footer-description">
                Transforming restaurants with AI-powered WhatsApp ordering
                solutions.
              </p>
            </div>

            <div className="footer-col">
              <h4 className="footer-heading">Product</h4>
              <ul className="footer-links">
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#solutions">Solutions</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4 className="footer-heading">Support</h4>
              <ul className="footer-links">
                <li>
                  <a href="#resources">Resources</a>
                </li>
                <li>
                  <a href="#contact">Contact</a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <button type="button" onclick="openAbout()">
                    About
                  </button>
                </li>
                <li>
                  <button type="button" onclick="openBlog()">
                    Blog
                  </button>
                </li>
                <li>
                  <button type="button" onclick="openCareers()">
                    Careers
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-separator"></div>

          <div className="footer-bottom">
            <p>© 2025 mealinkr. All rights reserved.</p>
            <div className="footer-bottom-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
